import useEnv from './useEnv'
import useUrls from './useUrls'
import useRestNode from './useRestNode'

/**
 * The purpose of this composition function is to encapsulate the logic needed to build common breadcrumb items to be
 * displayed in the BreadCrumb.vue component.  Some of the breadcrumb construction is boilerplate.  However, each
 * app will need to implement a piece of the puzzle.  The buildCustomItems function in the  BreadCrumb.vue component
 * will need to be implemented for each application that uses the useBreadCrumbContext api.  This function will supply items
 * contextual to a given application.
 *
 * The reason the buildCustomItems function is implemented in the BreadCrumb component is that some creadCrumb items depend
 * on the processing at server render of other composition function data fetches that end up in the model.  It is simply much
 * easier to access the needed data in the component at render rather than trying to figure out how to get the right data
 * to the use-breadCrumbContext composition function.
 */
export default function useBreadCrumbs(profile, context) {

    const { get } = useRestNode()
    const { safelinkEndpoint } = useEnv()

    const { buildUrl } = useUrls(context)
    const breadCrumbs = []

    /**
     * If the user came in through a box canyon and supplied the safelink name as part of the URL, lookup the safelink information
     * so that we can supply the correct name and URL to the safelink's launch page.
     */
    const getNetworkInfo = async (name) => {
    if (name) {
        let result = {}
        try {
            const url = `${safelinkEndpoint}/name/${name}`
            //console.log("GetNetworkinfo url", url)
            result = await get(url)
        } catch (error) {
        console.log(error)
        }
        return result
    }
    return null
    }

    /**
     * Build out the home item
     */
    const buildHomeItem = () => {
        const home = { name: 'Home', href: profile && profile.isAuthenticated ? `/my/plan-overview/` : `/` }
        //breadCrumbs.push(home)
        return home
    }

    /**
     * Builds out a network item.  This is only valid if the user came in through a box canyon and supplied the group key
     * in the request parameters.
     */
    const buildNetworkItem = async () => {
        const result = await getNetworkInfo(context.safelink)
        // console.log("NetworkItem =", result)
        const networkItem = { name: result.value.description + ' networks', href: result.value.launchPageUrl }
        if (result.value.description != null && result.value.launchPageUrl != '') breadCrumbs.push(networkItem)
    }

    /**
     * Build out the find-care landing page item.
     */
    const buildFindCareLandingItem = () => {
        let findCareLanding = { name: 'Find care', href: buildUrl('/fcp/csearch/', null, false).toString() }
        if (context.networkId && !context.safelink) {
            findCareLanding = {
                name: 'Find care',
                href: buildUrl(
                    '/fcp/csearch/',
                    {
                    networkId: context.networkId,
                    asOfDate: context.asOfDate,
                    },
                    false
                ).toString(),
            }
        }
        if (context.safelink) {
            findCareLanding = {
            name: 'Find care',
            href: buildUrl(
                '/fcp/csearch/',
                {
                safelink: context.safelink,
                empId: context.empId,
                networkId: context.networkId,
                asOfDate: context.asOfDate,
                groupNetworkName: context.groupNetworkName,
                groupNetworkUrl: context.groupNetworkUrl,
                marketingGroup: context.marketingGroup,
                },
                false
            ).toString(),
            }
        }
        return findCareLanding
    }

    const buildBreadCrumbs = async () => {
        if (process.server) {
            try {
                breadCrumbs.push(homeItem)

                //Check to see whether we have a safelink attribute included as a parameter on the request.  We should only
                //have a safelink item on the request if the user came in through a 'box canyon'.  In this case, we will want
                //to insert an item to the safelink's landing page.
                if (context.safelink && !profile.isAuthenticated) {
                    await buildNetworkItem()
                }

                breadCrumbs.push(landingItem)
                buildCustomItems()

            } catch (error) {
            console.log(error)
            throw error
            }

            return breadCrumbs
        }

        return null
    }

    /**
     * This function is used to build out custom items for the page.  Each page will need to customize this function.  This
     * should be the ONLY place of customization for breadcrumb URLS for any given application.
     */
    const buildCustomItems = () => {
        const searchResultsItem = { name: 'Search results', href: buildUrl('/fcp/csearch').toString() }
        if (context.latLong || profile.isAuthenticated) {
            breadCrumbs.push(searchResultsItem)
        }
    }    

    const landingItem = buildFindCareLandingItem()
    const homeItem = buildHomeItem()

    return { buildBreadCrumbs }
}